<template>
    <div class="abandoned-cart-recovery-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <ProBanner type="Premium" />

            <!-- Header -->
            <Header :title="$t('marketing.abandoned_cart.title') || 'Relance panier abandonné'" />

            <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row ppv2-gap-8">
                <!-- Left Column -->
                <div class="ppv2-w-full lg:ppv2-w-[65%]">
                    <v-card
                        class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        :style="{ padding: normalizeSize(24, true) }"
                    >
                        <div class="ppv2-space-y-6">
                            <!-- Email Subject -->
                            <CustomInput
                                :label="$t('marketing.abandoned_cart.subject')"
                                v-model="form.subject"
                                :placeholder="$t('marketing.abandoned_cart.subject_placeholder')"
                                :required="true"
                                :disabled="!canConfigure"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeFontSize(15)}`"
                            />

                            <!-- Message Body (BaseTextarea) -->
                            <BaseTextarea
                                v-model="form.message"
                                :label="$t('marketing.abandoned_cart.message_body')"
                                :required="true"
                                :rows="10"
                                :useEditor="true"
                                :disabled="!canConfigure"
                                :customSizeLabel="15"
                                customLabelClass="poppinsLight ppv2-text-black"
                                ref="messageEditor"
                            />

                            <!-- Variables -->
                            <div class="variables-container">
                                <p class="poppinsLight ppv2-text-black ppv2-mb-3" :style="{ fontSize: normalizeFontSize(14) }">
                                    {{
                                        $t('marketing.abandoned_cart.usable_variables') ||
                                        'Variables utilisables dans votre message'
                                    }}
                                </p>
                                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-2">
                                    <button
                                        v-for="variable in variables"
                                        :key="variable.tag"
                                        type="button"
                                        :disabled="!canConfigure"
                                        @click="insertVariable(variable.tag)"
                                        class="ppv2-px-3 ppv2-py-1 ppv2-rounded-lg ppv2-bg-[#7D2DBD] !ppv2-text-white poppinsRegular ppv2-text-xs hover:ppv2-bg-opacity-90 transition-all"
                                    >
                                        {{ variable.label }}
                                    </button>
                                </div>
                            </div>

                            <!-- Button Text -->
                            <CustomInput
                                :label="$t('marketing.abandoned_cart.button_text')"
                                v-model="form.button_text"
                                :placeholder="$t('marketing.abandoned_cart.button_text_placeholder')"
                                :required="true"
                                :disabled="!canConfigure"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeFontSize(15)}`"
                            />
                        </div>
                    </v-card>
                </div>

                <!-- Right Column -->
                <div class="ppv2-w-full lg:ppv2-w-[35%]">
                    <v-card
                        class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        :style="{ padding: normalizeSize(24, true) }"
                    >
                        <div class="ppv2-space-y-8">
                            <!-- Activation Toggle -->
                            <div class="ppv2-p-4 ppv2-rounded-xl ppv2-bg-[#E6F7F0] ppv2-bg-opacity-50">
                                <BaseSwitch
                                    v-model="form.is_active"
                                    :label="form.is_active ? $t('common.enabled') : $t('common.disabled')"
                                    :boxed="true"
                                    :variant="form.is_active ? 'success' : 'gray'"
                                    :disabled="!canConfigure"
                                    :customLabelStyle="`font-size: ${normalizeFontSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>

                            <!-- Sending Delay -->
                            <div class="delay-container">
                                <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-2">
                                    <label class="poppinsLight ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(15) }">
                                        {{ $t('marketing.abandoned_cart.sending_delay') }}
                                    </label>
                                </div>
                                <BaseSelect
                                    v-model="form.sending_delay"
                                    :options="delayOptions"
                                    :placeholder="$t('marketing.abandoned_cart.select_delay') || 'Sélectionner un délai'"
                                    :disabled="!canConfigure"
                                    class="v3-select-custom"
                                />
                            </div>
                        </div>
                    </v-card>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                <div class="ppv2-w-full md:ppv2-w-[63%]">
                    <div
                        class="ppv2-flex ppv2-justify-end ppv2-items-center"
                        :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                    >
                        <div class="ppv2-flex ppv2-gap-4">
                            <BaseButton
                                variant="outline"
                                @click="openPreview"
                                :customHeight="48"
                                customClass="!ppv2-px-8 !ppv2-border-[#7D2DBD] !ppv2-text-[#7D2DBD] hover:!ppv2-bg-[#7D2DBD] hover:!ppv2-text-white"
                            >
                                {{ $t('common.preview') }}
                            </BaseButton>
                            <BaseButton
                                variant="primary"
                                @click="save"
                                :loading="isSaving"
                                :disabled="!canConfigure"
                                :customHeight="48"
                                customClass="!ppv2-px-10 !ppv2-bg-[#2D3192] hover:!ppv2-bg-opacity-90"
                            >
                                {{ $t('common.save') || 'Enregistrer' }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Preview Modal -->
        <AbandonedCartPreviewModal v-if="showPreview" :show="showPreview" :email-data="form" @close="showPreview = false" />
    </div>
</template>

<script>
import axios from 'axios'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import AbandonedCartPreviewModal from './AbandonedCartPreviewModal.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import normalizeSize from '../../../../utils/normalizeSize.js'
import { Star } from 'lucide-vue'
import Header from '../../../Shared/Header.vue'
import ProBanner from '../../../Shared/ProBanner.vue'

export default {
    name: 'AbandonedCartRecovery',
    components: {
        CustomInput,
        BaseTextarea,
        BaseSelect,
        BaseSwitch,
        BaseButton,
        BackButton,
        AbandonedCartPreviewModal,
        Star,
        Header,
        ProBanner,
    },
    props: {
        userId: { type: Number, default: null },
        token: { type: String, default: '' },
    },
    computed: {
        apiHeaders() {
            return { headers: { Authorization: `Bearer ${this.token}` } }
        },
        variables() {
            const isFr = this.$i18n.locale === 'fr'
            return [
                { label: isFr ? 'Prénom' : 'Firstname', tag: isFr ? '{{Prénom}}' : '{{firstname}}' },
                { label: isFr ? 'Nom' : 'Lastname', tag: isFr ? '{{Nom}}' : '{{lastname}}' },
                {
                    label: isFr ? 'Date de réservation' : 'Booking date',
                    tag: isFr ? '{{Date de réservation}}' : '{{booking_date}}',
                },
                {
                    label: isFr ? 'Heure de réservation' : 'Booking time',
                    tag: isFr ? '{{Heure de réservation}}' : '{{booking_time}}',
                },
                { label: isFr ? 'Établissement' : 'Establishment', tag: isFr ? '{{Établissement}}' : '{{establishment}}' },
            ]
        },
    },
    data() {
        return {
            isSaving: false,
            isLoading: false,
            showPreview: false,
            // Niveau d'offre renvoyé par l'API — le serveur est seul juge. Repli RESTRICTIF :
            // si la lecture échoue, la page reste en lecture seule plutôt que d'ouvrir une
            // écriture que le serveur refusera de toute façon.
            isPremium: false,
            canConfigure: false,
            form: {
                subject: 'Faites vite, ce créneau est très demandé !',
                message: `Bonjour {{Prénom}},

Vous n’êtes plus qu’à un clic de confirmer votre réservation. Votre créneau est toujours disponible, mais il peut être réservé par quelqu'un d'autre à tout moment.

Nous avons hâte de vous accueillir !

À bientôt,
L’équipe {{Établissement}}`,
                button_text: 'Finaliser ma réservation',
                // Défaut RESTRICTIF : l'écran ne doit jamais annoncer une relance active tant que
                // le serveur ne l'a pas confirmé. Un tenant hors Premium, ou dont le chargement
                // échoue, verrait sinon « Activé » alors qu'aucun email ne part.
                is_active: false,
                sending_delay: '1_hour',
            },

            delayOptions: [
                { label: '30 minutes', value: '30_min' },
                { label: '1 heure', value: '1_hour' },
                { label: '2 heures', value: '2_hours' },
                { label: '24 heures', value: '24_hours' },
            ],
        }
    },
    mounted() {
        this.loadSettings()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async loadSettings() {
            this.isLoading = true
            try {
                const response = await axios.get('/api/v3/abandoned-cart/settings', this.apiHeaders)
                this.form.subject = response.data.subject || this.form.subject
                this.form.message = response.data.message || this.form.message
                this.form.button_text = response.data.button_text || this.form.button_text
                // `=== true` et non `?? true` : seule une confirmation explicite du serveur allume
                // l'interrupteur. Hors Premium l'API renvoie délibérément `false`, sans toucher à la
                // valeur stockée.
                this.form.is_active = response.data.is_active === true
                this.form.sending_delay = response.data.sending_delay || '1_hour'
                this.isPremium = response.data.is_premium === true
                this.canConfigure = response.data.can_configure === true
            } catch (e) {
                // Échec de lecture : on reste sur les défauts restrictifs (relance affichée comme
                // inactive, écran en lecture seule) plutôt que de promettre un état non vérifié.
                this.form.is_active = false
                this.canConfigure = false
            } finally {
                this.isLoading = false
            }
        },
        insertVariable(tag) {
            const baseTextarea = this.$refs.messageEditor
            if (baseTextarea && baseTextarea.descriptionEditorInstance) {
                const instance = baseTextarea.descriptionEditorInstance
                const viewFragment = instance.data.processor.toView(tag)
                const modelFragment = instance.data.toModel(viewFragment)
                instance.model.insertContent(modelFragment)
            } else {
                this.form.message += ` ${tag}`
            }
        },
        openPreview() {
            this.showPreview = true
        },
        async save() {
            // Garde d'affichage seulement : le refus qui fait foi est celui du serveur (403 sur
            // AbandonedCartSettingRequest::authorize).
            if (!this.canConfigure) {
                this.$toast.warning(this.$t('marketing.abandoned_cart.premium_required_body'))
                return
            }

            this.isSaving = true

            try {
                await axios.post('/api/v3/abandoned-cart/settings', this.form, this.apiHeaders)
                // `common.saved_success` n'existe dans aucune locale : `$t()` renvoyait alors la clé
                // elle-même, qui est une chaîne NON VIDE — le `||` ne se déclenchait donc jamais et
                // le toast affichait « common.saved_success » en clair. On réutilise une clé
                // existante, dont le libellé correspond exactement à cet écran de configuration.
                this.$toast.success(this.$t('common.config_updated_success'))
            } catch (e) {
                const msg = e.response?.data?.message || "Une erreur est survenue lors de l'enregistrement."
                this.$toast.error(msg)
            } finally {
                this.isSaving = false
            }
        },
        goBack() {
            window.history.back()
        },
    },
}
</script>
